import {
  isKnownThreadPullRequestStackMember,
  resolveThreadPullRequestChains,
  visibleThreadPullRequests,
} from "@afar-research/afar";
import type { ScopedThreadRef } from "@afar-research/contracts";
import type { ThreadPullRequestLink } from "@afar-research/contracts";
import {
  GitPullRequestArrowIcon,
  LayersIcon,
  LinkIcon,
  MoreHorizontalIcon,
  PlusIcon,
} from "lucide-react";
import { useCallback, useMemo, type MouseEvent } from "react";

import { writeTextToClipboard } from "../../hooks/useCopyToClipboard";
import { cn } from "../../lib/utils";
import { useServerConfigs, useThreadShell } from "../../state/entities";
import { threadEnvironment } from "../../state/threads";
import { useAtomCommand } from "../../state/use-atom-command";
import { formatRelativeTimeLabel } from "../../timestampFormat";
import { Button } from "../ui/button";
import { Menu, MenuGroup, MenuGroupLabel, MenuItem, MenuPopup, MenuTrigger } from "../ui/menu";
import { ScrollArea } from "../ui/scroll-area";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { openLinkPullRequestDialog } from "./LinkPullRequestDialog";
import { PullRequestsUnavailableState } from "./PullRequestsUnavailableState";
import { showPullRequestLinkContextMenu } from "./pullRequestLinkContextMenu";
import { supportsThreadPullRequests } from "./threadPullRequestCapability";
import { pullRequestListLines, type PullRequestListLine } from "./pullRequestListLines";
import {
  PullRequestActorAvatar,
  PullRequestApprovalGlyph,
  PullRequestChecksGlyph,
  PullRequestDiffStat,
  PullRequestStateGlyph,
} from "./pullRequestPresentation";

/**
 * A thread's linked pull requests, beside its conversation. The collection is durable state the
 * owning Environment holds, so this panel only reads it and asks for transitions: it never
 * decides what membership is, and it shows nothing it has not actually observed.
 */

const SOURCE_LABELS: Record<ThreadPullRequestLink["source"], string> = {
  manual: "Linked by you",
  created: "Created from this thread",
  agent: "Linked by the agent",
  stack: "Found in the stack",
  "stack-dismissed": "Dismissed",
};

/** Following T3Code: a modifier-click leaves the link alone and opens it on the host. */
export function shouldOpenPullRequestExternally(
  event: Pick<MouseEvent, "metaKey" | "ctrlKey">,
): boolean {
  return event.metaKey || event.ctrlKey;
}

function LinkRow({
  line,
  dismisses,
  onOpen,
  onOpenLayer,
  onUnlink,
}: {
  readonly line: PullRequestListLine;
  /** Whether removing this link will tombstone it, which is what the action must say. */
  readonly dismisses: boolean;
  readonly onOpen: (link: ThreadPullRequestLink) => void;
  /** Opens another layer of this link's stack, which lives in the same repository. */
  readonly onOpenLayer: (number: number) => void;
  readonly onUnlink: (link: ThreadPullRequestLink) => void;
}) {
  const { link, depth, stack } = line;
  const observation = link.observation;
  return (
    <div
      className="group/pr-row flex items-center gap-2 rounded-md py-1 pr-1 hover:bg-accent/60"
      // Each layer steps in under the one it targets. The step is capped: beyond a few layers
      // the indent only says "still in the stack", which the connector line already does, and a
      // sixteen-layer stack would otherwise stair-step off the right edge.
      style={{ paddingLeft: `${0.5 + Math.min(depth, 3) * 1.25}rem` }}
    >
      {depth > 0 ? <span aria-hidden className="-ml-2 h-6 w-px shrink-0 bg-border/70" /> : null}
      {observation === null ? (
        <GitPullRequestArrowIcon
          aria-label="Waiting for host state"
          className="size-4 shrink-0 text-muted-foreground"
        />
      ) : (
        <PullRequestStateGlyph state={observation.state} isDraft={observation.isDraft} />
      )}
      {/* Opening a pull request keeps the reader in the thread they were reading. The anchor
          stays a real link, so a modifier-click still reaches the host. */}
      <a
        href={link.url}
        rel="noreferrer"
        target="_blank"
        className="min-w-0 flex-1"
        onClick={(event) => {
          if (shouldOpenPullRequestExternally(event)) return;
          event.preventDefault();
          onOpen(link);
        }}
      >
        <span className="flex min-w-0 items-center gap-1.5">
          <Tooltip>
            <TooltipTrigger
              render={
                <span
                  className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground"
                  onContextMenu={(event) => {
                    event.preventDefault();
                    void showPullRequestLinkContextMenu({
                      url: link.url,
                      position: { x: event.clientX, y: event.clientY },
                    });
                  }}
                />
              }
            >
              #{link.number}
            </TooltipTrigger>
            <TooltipPopup>
              {SOURCE_LABELS[link.source]} · {formatRelativeTimeLabel(link.linkedAt)}
            </TooltipPopup>
          </Tooltip>
          <span className="min-w-0 flex-1 truncate text-sm">
            {observation?.title ?? link.repository}
          </span>
          {/* Review verdict, checks, then diff counts. Each is absent rather than neutral when
              the host said nothing, so a row without them reads as unknown, not as fine. */}
          <span className="ml-auto flex shrink-0 items-center gap-1.5 text-[11px]">
            {observation?.state === "open" && observation.reviewDecision === "approved" ? (
              <PullRequestApprovalGlyph />
            ) : null}
            {observation?.state === "open" && observation.reviewDecision === "changes-requested" ? (
              <span className="text-amber-600/90 dark:text-amber-400/80">Changes requested</span>
            ) : null}
            {observation?.state === "open" && observation.mergeability === "conflicting" ? (
              <span className="text-destructive">Conflicts</span>
            ) : null}
            {observation?.checksState ? (
              <PullRequestChecksGlyph state={observation.checksState} />
            ) : null}
            <PullRequestDiffStat
              additions={observation?.additions ?? 0}
              deletions={observation?.deletions ?? 0}
              className="font-mono"
            />
          </span>
        </span>
        <span className="flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
          {stack ? (
            // The layers are already on the link, so naming them costs no read: following
            // T3Code's stack popover, the count opens into what it is counting.
            <Menu>
              <MenuTrigger
                render={
                  <button
                    aria-label={`${stack.size} in this stack`}
                    className="inline-flex shrink-0 items-center gap-0.5 rounded px-0.5 text-foreground/70 hover:bg-accent"
                    onClick={(event) => event.stopPropagation()}
                    type="button"
                  />
                }
              >
                {stack.kind === "native" ? (
                  <LayersIcon aria-hidden className="size-3" />
                ) : (
                  <GitPullRequestArrowIcon aria-hidden className="size-3" />
                )}
                {stack.size}
              </MenuTrigger>
              <MenuPopup align="start" side="bottom">
                <MenuGroup>
                  <MenuGroupLabel>
                    {stack.kind === "native"
                      ? `GitHub stack of ${stack.size}: merging a layer lands the ones below it.`
                      : `${stack.size} pull requests chained by base branch.`}
                  </MenuGroupLabel>
                  {/* The count on the row is what this thread links; the host's stack can hold
                    layers nobody linked here, and the list below is the host's. */}
                  {link.stack !== null && link.stack.layers.length !== stack.size ? (
                    <MenuGroupLabel>
                      {link.stack.layers.length} layers on the host, {stack.size} linked here.
                    </MenuGroupLabel>
                  ) : null}
                  {/* Top first, which is how a reader looks at a stack: the layer they are on,
                    then what it is sitting on. */}
                  {[...(link.stack?.layers ?? [])].toReversed().map((layer) => (
                    <MenuItem key={layer.number} onClick={() => onOpenLayer(layer.number)}>
                      <span className="flex min-w-0 items-center gap-2">
                        <PullRequestStateGlyph state={layer.state} isDraft={false} />
                        <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
                          #{layer.number}
                        </span>
                        <span className="min-w-0 truncate font-mono text-xs">
                          {layer.headBranch}
                        </span>
                      </span>
                    </MenuItem>
                  ))}
                </MenuGroup>
              </MenuPopup>
            </Menu>
          ) : null}
          {observation?.author ? (
            <span className="inline-flex shrink-0 items-center gap-1">
              <PullRequestActorAvatar actor={observation.author} className="size-3.5" />
              <span className="max-w-28 truncate">{observation.author.login}</span>
            </span>
          ) : null}
          <span className="truncate font-mono">
            {observation === null
              ? `${link.host}/${link.repository}`
              : `${observation.headBranch} → ${observation.baseBranch}`}
          </span>
          {/* A failed read keeps the observation it could not replace, so say the state is old
              rather than letting it read as current. */}
          {link.observationUnavailable === true ? (
            <Tooltip>
              <TooltipTrigger
                render={<span className="ml-auto shrink-0 text-warning-foreground" />}
              >
                Could not read
              </TooltipTrigger>
              <TooltipPopup>
                {observation === null
                  ? "This AFAR Environment could not read this pull request."
                  : `Showing what was read ${formatRelativeTimeLabel(observation.observedAt)}.`}
              </TooltipPopup>
            </Tooltip>
          ) : observation?.updatedAt ? (
            <span className="ml-auto shrink-0">
              {formatRelativeTimeLabel(observation.updatedAt)}
            </span>
          ) : null}
        </span>
      </a>
      <Menu>
        <MenuTrigger
          render={
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label={`Actions for #${link.number}`}
              className={cn(
                "opacity-0 group-hover/pr-row:opacity-100 data-[popup-open]:opacity-100",
              )}
            >
              <MoreHorizontalIcon className="size-3.5" />
            </Button>
          }
        />
        <MenuPopup align="end" side="bottom">
          <MenuItem onClick={() => void writeTextToClipboard(link.url, "link")}>Copy link</MenuItem>
          <MenuItem onClick={() => onOpen(link)}>Open</MenuItem>
          <MenuItem onClick={() => window.open(link.url, "_blank", "noreferrer")}>
            Open on GitHub
          </MenuItem>
          <MenuItem onClick={() => onUnlink(link)}>
            {/* Removing a pull request AFAR knows to be in a stack tombstones it so discovery
                cannot put it back, however the user came to link it. The label is derived from
                the same rule the Environment applies, so it cannot promise the other outcome. */}
            {dismisses ? "Dismiss from thread" : "Unlink from thread"}
          </MenuItem>
        </MenuPopup>
      </Menu>
    </div>
  );
}

export function ThreadPullRequestsPanel({
  threadRef,
  onOpenPullRequest,
}: {
  readonly threadRef: ScopedThreadRef;
  /** Opens a linked pull request beside this conversation. */
  readonly onOpenPullRequest: (link: ThreadPullRequestLink) => void;
}) {
  const configs = useServerConfigs();
  const supported = supportsThreadPullRequests(configs, threadRef.environmentId);
  const thread = useThreadShell(threadRef);
  const unlink = useAtomCommand(threadEnvironment.unlinkPullRequest, { reportFailure: true });
  // The dialog itself is mounted once by the chat view, so the command palette can open it for
  // this thread too. This panel only asks.
  const openLinkDialog = useCallback(() => openLinkPullRequestDialog(threadRef), [threadRef]);
  const links = useMemo(
    () => visibleThreadPullRequests(thread?.pullRequests ?? []),
    [thread?.pullRequests],
  );
  const lines = useMemo(() => pullRequestListLines(resolveThreadPullRequestChains(links)), [links]);
  const revision = thread?.pullRequestsRevision ?? 0;
  const handleUnlink = useCallback(
    (link: ThreadPullRequestLink) => {
      void unlink({
        environmentId: threadRef.environmentId,
        input: {
          threadId: threadRef.threadId,
          host: link.host,
          repository: link.repository,
          number: link.number,
          expectedRevision: revision,
        },
      });
    },
    [revision, threadRef, unlink],
  );
  const openCount = useMemo(
    () =>
      links.filter((link) => link.observation === null || link.observation.state === "open").length,
    [links],
  );
  // The most recent successful read of any link. An old value stays labelled as old rather than
  // being refreshed by a failed read, so this never claims work that did not happen.
  const lastObservedAt = useMemo(() => {
    let latest: string | null = null;
    for (const link of links) {
      const at = link.observation?.observedAt;
      if (at !== undefined && (latest === null || at > latest)) latest = at;
    }
    return latest;
  }, [links]);

  if (!supported) {
    return (
      <PullRequestsUnavailableState
        title="Linked Pull Requests unavailable"
        error="This AFAR Environment does not support linking pull requests to a thread."
      />
    );
  }

  // A thread this client does not hold yet says nothing about its collection. Reading that
  // absence as "empty" would state a fact nobody read, and offer to link against a thread the
  // client cannot name a revision for, so wait rather than answer.
  if (thread === null) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
        <p className="text-xs text-muted-foreground">Loading this thread…</p>
      </div>
    );
  }

  if (links.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
        <LinkIcon aria-hidden className="size-6 text-muted-foreground/60" />
        <p className="text-sm font-medium">No linked Pull Requests</p>
        <p className="max-w-60 text-xs text-muted-foreground">
          Pull requests the agent opens from this thread land here. Link one yourself from a URL or
          a number.
        </p>
        <Button size="sm" variant="outline" onClick={openLinkDialog}>
          <PlusIcon className="size-3.5" />
          Link Pull Request
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <ScrollArea className="min-h-0 flex-1">
        <div className="flex flex-col p-1.5">
          {lines.map((line) => (
            <LinkRow
              key={`${line.link.host}/${line.link.repository}#${line.link.number}`}
              line={line}
              dismisses={isKnownThreadPullRequestStackMember({ links, revision }, line.link)}
              onOpen={onOpenPullRequest}
              onOpenLayer={(number) =>
                onOpenPullRequest({
                  ...line.link,
                  number,
                  url: `https://${line.link.host}/${line.link.repository}/pull/${number}`,
                })
              }
              onUnlink={handleUnlink}
            />
          ))}
        </div>
      </ScrollArea>
      <footer className="flex items-center justify-between border-t border-border/60 px-2 py-1.5 text-[.7rem] text-muted-foreground">
        <span>
          {openCount} open · {links.length} linked
          {lastObservedAt ? ` · read ${formatRelativeTimeLabel(lastObservedAt)}` : ""}
        </span>
        <Button size="xs" variant="ghost" onClick={openLinkDialog}>
          <PlusIcon className="size-3.5" />
          Link
        </Button>
      </footer>
    </div>
  );
}
